import React from 'react';

const STRINGS = {
  bm: {
    title: 'Ralat berlaku',
    body: 'Sila refresh halaman. Jika masalah berterusan, cuba clear cache browser.',
    retry: 'Refresh',
    supportTitle: 'Masalah masih berterusan?',
    supportBody: 'Hubungi support:',
    loading: 'Memuatkan semula...',
  },
  en: {
    title: 'Something went wrong',
    body: 'Please refresh the page. If the issue persists, try clearing your browser cache.',
    retry: 'Refresh',
    supportTitle: 'Still facing issues?',
    supportBody: 'Contact support:',
    loading: 'Retrying...',
  },
};

// Detect language from URL — /worldwide/* = English, otherwise BM
const getLang = () => {
  try {
    return window.location.pathname.startsWith('/worldwide') ? 'en' : 'bm';
  } catch {
    return 'bm';
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, autoRetrying: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Auto-recover from chunk-load errors (common after deploy — stale JS bundle)
    const msg = String(error?.message || '');
    const isChunkError =
      msg.includes('Loading chunk') ||
      msg.includes('ChunkLoadError') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('dynamically imported module');

    if (isChunkError) {
      const key = 'fira_chunk_retry_ts';
      const last = Number(sessionStorage.getItem(key) || 0);
      // Retry once per 60s window to avoid infinite reload loops
      if (Date.now() - last > 60_000) {
        sessionStorage.setItem(key, String(Date.now()));
        this.setState({ autoRetrying: true });
        setTimeout(() => window.location.reload(), 400);
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const lang = getLang();
    const t = STRINGS[lang];

    if (this.state.autoRetrying) {
      return (
        <div style={styles.container}>
          <div style={{ ...styles.icon, color: '#ec4899' }}>↻</div>
          <p style={styles.body}>{t.loading}</p>
        </div>
      );
    }

    return (
      <div style={styles.container}>
        <div style={styles.icon}>!</div>
        <h2 style={styles.title}>{t.title}</h2>
        <p style={styles.body}>{t.body}</p>

        <button
          data-testid="error-boundary-reload"
          onClick={() => window.location.reload()}
          style={styles.button}
        >
          {t.retry}
        </button>

        <div style={styles.support}>
          <div style={styles.supportTitle}>{t.supportTitle}</div>
          <div style={styles.supportBody}>
            {t.supportBody}{' '}
            <a
              href={lang === 'en' ? 'https://t.me/FIRAWorldBot' : 'https://t.me/firasmartdesk_bot'}
              style={styles.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {lang === 'en' ? '@FIRAWorldBot' : '@firasmartdesk_bot'}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '24px',
    textAlign: 'center',
    background: '#0a0a0a',
    color: '#e0e0e0',
  },
  icon: {
    fontSize: '48px',
    marginBottom: '16px',
    color: '#f87171',
    fontWeight: 'bold',
  },
  title: {
    fontSize: '18px',
    marginBottom: '8px',
    color: '#f87171',
  },
  body: {
    fontSize: '14px',
    color: '#999',
    marginBottom: '24px',
    maxWidth: '320px',
    lineHeight: 1.5,
  },
  button: {
    padding: '12px 32px',
    background: '#ec4899',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  support: {
    marginTop: '32px',
    padding: '16px 20px',
    borderRadius: '8px',
    background: 'rgba(236, 72, 153, 0.08)',
    border: '1px solid rgba(236, 72, 153, 0.2)',
    maxWidth: '320px',
  },
  supportTitle: {
    fontSize: '12px',
    color: '#f87171',
    fontWeight: 600,
    marginBottom: '4px',
  },
  supportBody: {
    fontSize: '12px',
    color: '#cbd5e1',
  },
  link: {
    color: '#ec4899',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default ErrorBoundary;
